import { Button, ButtonProps } from "@cmp-src/primitives/button";
import { LucideIcon } from "lucide-react";
import { cn } from "@gettactic/helpers";
import * as React from "react";

// Color variants for semantic icon buttons (table actions, etc.)
const colorVariants = {
  success: "text-green-600 hover:bg-green-50 hover:text-green-700",
  danger: "text-red-500 hover:bg-red-50 hover:text-red-700",
  neutral: "text-neutral-500 hover:bg-neutral-50 hover:text-neutral-700"
};

type ColorVariant = keyof typeof colorVariants;

// Size presets for icon buttons
const sizePresets = {
  xs: { button: "h-6 w-6", icon: "h-3 w-3" },
  sm: { button: "h-8 w-8", icon: "h-4 w-4" },
  md: { button: "h-10 w-10", icon: "h-5 w-5" },
  lg: { button: "h-12 w-12", icon: "h-6 w-6" }
};

type SizePreset = keyof typeof sizePresets;

export interface IconButtonProps
  extends Omit<ButtonProps, "variant" | "size" | "children"> {
  /**
   * The icon to show. Pass a Lucide component (`icon={Trash}`) to get the size preset's
   * icon size, or a JSX element when the icon needs its own props; the preset size is
   * merged into its `className`.
   */
  icon: LucideIcon | React.ReactNode;
  /**
   * Any `Button` variant, or a semantic colour for row and table actions:
   *
   * - `neutral`: secondary row actions (Edit, Duplicate).
   * - `danger`: destructive row actions (Delete, Remove).
   * - `success`: positive row actions (Approve, Mark as done).
   *
   * The semantic colours render as a `ghost` button tinted with that colour.
   * @default "ghost"
   */
  variant?: ButtonProps["variant"] | ColorVariant;
  /**
   * Button and icon size: `xs` 24px (12px icon), `sm` 32px (16px), `md` 40px (20px),
   * `lg` 48px (24px).
   * @default "sm"
   */
  size?: SizePreset;
  /** Extra classes for the icon, merged after the size preset. */
  iconClassName?: string;
  /**
   * Dims the button to 30% until its row is hovered or the button gets keyboard focus.
   * The row must have Tailwind's `group` class.
   * @default false
   */
  showOnHover?: boolean;
}

const isColorVariant = (
  variant: string | null | undefined
): variant is ColorVariant => {
  return typeof variant === "string" && variant in colorVariants;
};

const isLucideIcon = (
  icon: LucideIcon | React.ReactNode
): icon is LucideIcon => {
  if (typeof icon === "function") return true;
  // forwardRef components (like Lucide icons) are objects with $$typeof and render
  if (
    typeof icon === "object" &&
    icon !== null &&
    "$$typeof" in icon &&
    "render" in icon
  ) {
    return true;
  }
  return false;
};

/**
 * A square button that shows only an icon, built on `Button`.
 *
 * - It has no visible text: always pass an `aria-label` describing the action.
 * - Use it for toolbar, header and row actions; use `Button` with `iconLeft` when the action needs a label.
 */
export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      icon,
      variant = "ghost",
      size = "sm",
      iconClassName,
      showOnHover = false,
      className,
      ...props
    },
    ref
  ) => {
    const sizeConfig = sizePresets[size];
    const isColor = isColorVariant(variant);
    const buttonVariant = isColor ? "ghost" : variant;

    const iconElement = isLucideIcon(icon)
      ? React.createElement(icon, {
          className: cn(sizeConfig.icon, iconClassName)
        })
      : React.isValidElement(icon)
        ? React.cloneElement(
            icon as React.ReactElement<{ className?: string }>,
            {
              className: cn(
                sizeConfig.icon,
                (icon as React.ReactElement<{ className?: string }>).props
                  ?.className,
                iconClassName
              )
            }
          )
        : icon;

    return (
      <Button
        ref={ref}
        variant={buttonVariant}
        size="icon"
        className={cn(
          sizeConfig.button,
          "p-0",
          showOnHover &&
            "opacity-30 transition-opacity duration-100 focus-visible:opacity-100 group-hover:opacity-100",
          isColor && colorVariants[variant],
          className
        )}
        {...props}
      >
        {iconElement}
      </Button>
    );
  }
);

IconButton.displayName = "IconButton";
